import { Cushion, Grid, MaxWidth, Rectangle } from '@pitchero/react-ui';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import RosBottomBanner from '../adverts/ros-bottom-banner';
import TopBannerVideoPlayerSwitch from '../adverts/top-banner-video-player-switch';
import LoadingBar from '../loading-bar';
import SeoHead from '../seo';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import ClubRouter from '../../lib/routing';
import useBreakpoint from '../../lib/use-breakpoint';
import CalendarComponent from '../matches/calendar';
import CalendarLoadingState from './calendar-loading-state';
import CalendarSummary from './calendar-summary';
import CalendarFilterBar from './filter-bar';

const Calendar = ({ calendar, club, eventType, isLoading, month, teamId, year, t }) => {
  const now = DateTime.now();
  const currentMonth = DateTime.fromObject({ year, month });
  const initialDate = now.month === currentMonth.month ? now : currentMonth.startOf('month');
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const isMobile = useBreakpoint();

  const onChangeTeamHandler = (selectedTeamId) => {
    ClubRouter.replaceClubRoute(club, 'calendar.month', {
      year,
      month,
      teamId: selectedTeamId,
      eventType,
    });
  };

  const onChangeMonthHandler = (isPrevious) => {
    const newMonth = isPrevious
      ? currentMonth.minus({ months: 1 })
      : currentMonth.plus({ months: 1 });
    setSelectedDate(newMonth.startOf('month'));
    ClubRouter.replaceClubRoute(club, 'calendar.month', {
      year: newMonth.year,
      month: newMonth.month,
      teamId,
      eventType,
    });
  };

  const onChangeEventTypeHandler = (selectedEventType) => {
    ClubRouter.replaceClubRoute(club, 'calendar.month', {
      year,
      month,
      teamId,
      eventType: selectedEventType,
    });
  };

  const onChangeDayHandler = (date) => {
    const dateTime = DateTime.fromFormat(date, 'yyyy-MM-dd');
    setSelectedDate(dateTime);
  };

  const { advertisingTier, sportId } = club;

  return (
    <>
      <SeoHead
        club={club}
        descriptionTranslationKey="common:calendar_description"
        titleTranslationKey="common:calendar_title"
        translationValues={{
          month: DateTime.fromObject({ year, month, day: 1 }).toFormat('LLLL yyyy'),
        }}
        route="calendar.month"
        routeParams={{ month, year }}
      />
      <CalendarFilterBar
        club={club}
        eventType={eventType}
        teamId={teamId}
        onChangeTeam={onChangeTeamHandler}
        onChangeMonth={onChangeMonthHandler}
        onChangeEventType={onChangeEventTypeHandler}
        isMobile={isMobile}
        t={t}
      />
      <Rectangle fill="#fff" component="div">
        <LoadingBar isLoading={isLoading} />
        <MaxWidth maxWidth={1366} center>
          <Cushion vertical="medium">
            <TopBannerVideoPlayerSwitch
              advertisingTier={advertisingTier}
              pageType="calendar.index"
              sportId={sportId}
            />
          </Cushion>
          {isLoading || !calendar ? (
            <CalendarLoadingState />
          ) : (
            <Grid
              columns="1fr"
              rowGap="medium"
              columnGap="large"
              responsive={[
                { minWidth: 'fullHeader', props: { columns: 'minmax(0, 900px) minmax(0, 450px)' } },
              ]}
              style={{ padding: '20px 0' }}
            >
              <CalendarComponent
                month={month}
                year={year}
                data={calendar?.days}
                validDates={[selectedDate.toFormat('yyyy-MM-dd')]}
                onMonthClick={onChangeMonthHandler}
                onDayClick={onChangeDayHandler}
                isMobile={isMobile}
              />
              <CalendarSummary club={club} data={calendar?.days} selectedDate={selectedDate} />
            </Grid>
          )}
          <Cushion vertical="large">
            <RosBottomBanner />
          </Cushion>
        </MaxWidth>
      </Rectangle>
    </>
  );
};

Calendar.defaultProps = {
  calendar: null,
  eventType: null,
  teamId: null,
};

Calendar.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  calendar: PropTypes.shape(),
  eventType: PropTypes.oneOf(['events', 'fixtures', 'training']),
  isLoading: PropTypes.bool.isRequired,
  month: PropTypes.number.isRequired,
  teamId: PropTypes.number,
  year: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
};

export default Calendar;
